import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell } from "@/components/PageShell";
import { Loader2, Check, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICING_CACHE_KEY = "facow_pricing_catalog_v3";
const CHAT_STORAGE_KEY = "facow_chat_session";

// Map a pricing-catalog cta → backend package_id understood by /api/payments/checkout
const STRIPE_PACKAGE_MAP = {
  subscription: "subscription_ai",
  recharge: "recharge_20",
};

// Ensure we have a chatbot session before initiating Stripe checkout — the backend
// attaches the credit grant (one-shot or monthly subscription) to that session.
async function ensureChatSession(locale) {
  const cached = localStorage.getItem(CHAT_STORAGE_KEY);
  if (cached) {
    try {
      await axios.get(`${API}/chat/credits/${cached}`);
      return cached;
    } catch (e) {
      localStorage.removeItem(CHAT_STORAGE_KEY);
    }
  }
  const r = await axios.post(`${API}/chat/init`, { locale });
  localStorage.setItem(CHAT_STORAGE_KEY, r.data.session_id);
  return r.data.session_id;
}

const CATEGORIES_FR = {
  digital: { title: "Écosystème Digital", sub: "La voie d'entrée FACOW360" },
  accompagnements: { title: "Accompagnements & Conseils", sub: "Méthode FACOW360 — magnétisme & atelier cuisine" },
  geobiologie: { title: "Géobiologie", sub: "Géobiologie — Analyse de l'Habitat" },
};
const CATEGORIES_EN = {
  digital: { title: "Digital Ecosystem", sub: "The FACOW360 entry path" },
  accompagnements: { title: "Accompaniments & Advice", sub: "FACOW360 method — magnetism & cooking workshop" },
  geobiologie: { title: "Geobiology", sub: "Geobiology — Habitat Analysis" },
};

const unitLabel = (u, isFr) => {
  if (u === "month") return isFr ? "/ mois" : "/ month";
  if (u === "session") return isFr ? "/ séance" : "/ session";
  return "";
};

export default function Tarifs() {
  const { locale, t } = useLang();
  const isFr = locale === "fr";
  const CATS = isFr ? CATEGORIES_FR : CATEGORIES_EN;
  const [loadingCta, setLoadingCta] = useState(null); // item.key currently being processed

  const [catalog, setCatalog] = useState(() => {
    try {
      const cached = localStorage.getItem(PRICING_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [err, setErr] = useState("");

  const handleStripeCta = async (item) => {
    const pkg = STRIPE_PACKAGE_MAP[item.cta];
    if (!pkg) return;
    setLoadingCta(item.key);
    try {
      const sid = await ensureChatSession(locale);
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: pkg,
        session_id: sid,
        origin_url: window.location.origin,
      });
      window.location.assign(r.data.url);
    } catch (e) {
      setLoadingCta(null);
      setErr(e?.response?.data?.detail || e?.message || (isFr ? "Erreur Stripe" : "Stripe error"));
    }
  };

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const fetchPricing = async () => {
      try {
        const r = await axios.get(`${API}/pricing`, { timeout: 10000 });
        if (cancelled) return;
        if (r.data && typeof r.data === "object") {
          setCatalog(r.data);
          setErr("");
          try { localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify(r.data)); } catch (_e) { /* ignore quota errors */ }
        }
      } catch (e) {
        if (cancelled) return;
        attempts++;
        if (attempts < 5) {
          setTimeout(fetchPricing, Math.min(8000, 1000 * Math.pow(2, attempts)));
        } else {
          setErr(isFr ? "Connexion lente — réessai automatique." : "Slow connection — auto-retrying.");
        }
      }
    };
    fetchPricing();
    return () => { cancelled = true; };
  }, [isFr]);

  return (
    <PageShell
      testId="page-tarifs"
      title={isFr ? "Tarifs officiels" : "Official Pricing"}
      subtitle={isFr
        ? "Transparence totale. Tous les tarifs ci-dessous sont les tarifs officiels de la méthode FACOW360. Paiement sécurisé Stripe, acompte de 30 % à la commande pour les prestations physiques."
        : "Full transparency. Every price below is the official FACOW360 rate. Secure Stripe payment, 30% deposit at order for on-site services."}
    >
      {!catalog && !err && (
        <div className="flex items-center gap-2 text-slate-400" data-testid="tarifs-loading"><Loader2 className="animate-spin" size={16} /> {isFr ? "Chargement…" : "Loading…"}</div>
      )}
      {!catalog && err && <p className="text-amber-glow text-sm" data-testid="tarifs-err">{err}</p>}

      {catalog && (
        <div className="space-y-10">
          {Object.keys(CATS).map((catKey) => (
            <section key={catKey} data-testid={`tarifs-section-${catKey}`}>
              <div className="mb-5">
                <p className="label-overline mb-2 text-[#D4AF37]">{CATS[catKey].title}</p>
                <h2 className="font-display text-2xl md:text-3xl text-amber-glow tracking-tight">{CATS[catKey].sub}</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {(catalog[catKey] || []).map((item) => (
                  <div
                    key={item.key}
                    className="rounded-2xl p-6 border bg-gradient-to-b from-[#0A1128] to-[#0F1A3A] border-[#D4AF37]/30 hover:border-[#D4AF37]/70 transition relative overflow-hidden"
                    data-testid={`tarif-${item.key}`}
                  >
                    <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#D4AF37]/5 blur-3xl pointer-events-none" />
                    <p className="relative label-overline mb-3">{item.key.replace(/_/g, " ").toUpperCase()}</p>
                    <h3 className="relative font-display text-base md:text-lg text-slate-100 mb-3 leading-tight">
                      {isFr ? item.label_fr : item.label_en}
                    </h3>
                    {(isFr ? item.description_fr : item.description_en) && (
                      <p className="relative text-xs text-slate-400 leading-relaxed mb-4" data-testid={`tarif-${item.key}-desc`}>
                        {isFr ? item.description_fr : item.description_en}
                      </p>
                    )}
                    <div className="relative flex items-baseline gap-2 mb-5">
                      {item.price != null ? (
                        <>
                          <span className="font-display text-4xl md:text-5xl text-amber-glow tracking-tight">{item.price} €</span>
                          <span className="text-xs font-mono-tech uppercase tracking-widest text-slate-400">{unitLabel(item.unit, isFr)}</span>
                        </>
                      ) : (
                        <span className="font-display text-3xl md:text-4xl text-amber-glow tracking-tight">{isFr ? "Sur devis" : "On quote"}</span>
                      )}
                    </div>
                    <div className="relative flex gap-2">
                      {item.cta === "bilan" ? (
                        <Link to="/bilan" className="btn-gold !px-4 !py-2 !text-[11px]" data-testid={`tarif-cta-${item.key}`}>
                          {isFr ? "Commander" : "Order"}
                        </Link>
                      ) : item.cta === "subscription" ? (
                        <button
                          type="button"
                          onClick={() => handleStripeCta(item)}
                          disabled={loadingCta === item.key}
                          className="btn-gold !px-4 !py-2 !text-[11px] disabled:opacity-60"
                          data-testid={`tarif-cta-${item.key}`}
                        >
                          {loadingCta === item.key
                            ? (isFr ? "Redirection…" : "Redirecting…")
                            : (isFr ? "S'abonner" : "Subscribe")}
                        </button>
                      ) : item.cta === "recharge" ? (
                        <button
                          type="button"
                          onClick={() => handleStripeCta(item)}
                          disabled={loadingCta === item.key}
                          className="btn-primary !px-4 !py-2 !text-[11px] disabled:opacity-60"
                          data-testid={`tarif-cta-${item.key}`}
                        >
                          {loadingCta === item.key
                            ? (isFr ? "Redirection…" : "Redirecting…")
                            : (isFr ? "Recharger" : "Recharge")}
                        </button>
                      ) : (
                        <Link to="/devis" className="btn-primary !px-4 !py-2 !text-[11px]" data-testid={`tarif-cta-${item.key}`}>
                          {isFr ? "Demander un devis" : "Request a quote"}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="glass rounded-2xl p-7 md:p-8 border border-[#00F0FF]/20" data-testid="tarifs-logistics">
            <p className="label-overline mb-3">{isFr ? "Logistique & règles commerciales" : "Logistics & commercial rules"}</p>
            <ul className="space-y-2 text-sm text-slate-300">
              <li className="flex items-start gap-2"><Check size={14} className="text-[#00F0FF] mt-1" />{isFr ? "Frais de route : 0,69 € / km (tarif unique, au-delà de 20 km)." : "Travel: €0.69 / km (single rate, beyond 20 km)."}</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#00F0FF] mt-1" />{isFr ? "Acompte de 30 % à la commande pour toute prestation physique." : "30% deposit at order for every on-site service."}</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#00F0FF] mt-1" />{isFr ? "Paiement sécurisé Stripe — aucune étude engagée sans confirmation du règlement." : "Secure Stripe payment — no study engaged without confirmed settlement."}</li>
              <li className="flex items-start gap-2"><Check size={14} className="text-[#D4AF37] mt-1" />{isFr ? "TVA non applicable (Art. 293 B du CGI)." : "VAT not applicable (Art. 293 B French CGI)."}</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/devis" className="btn-gold" data-testid="tarifs-devis-cta">
                <FileText size={14} /> {isFr ? "Demander un devis spécifique" : "Request a custom quote"}
              </Link>
              <Link to="/contact" className="btn-primary" data-testid="tarifs-contact-cta">{t("nav.contact")}</Link>
            </div>
          </section>

          <p className="text-xs text-slate-500">{t("disclaimer")}</p>
        </div>
      )}
    </PageShell>
  );
}
